/**
 * RNR's Card, with the design file's own anatomy: one border, one soft shadow, radius-lg, a header
 * separated by a hairline and a footer sitting on --background so the action row separates without a
 * second rule. Cards never nest inside cards; a card that needs a card inside it is a list.
 */
import { Text, TextClassContext } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { Platform, View } from 'react-native';

function Card({ className, ...props }: React.ComponentProps<typeof View> & React.RefAttributes<View>) {
  return (
    <TextClassContext.Provider value="text-card-foreground">
      <View
        className={cn(
          'bg-card border-border overflow-hidden rounded-lg border',
          // Shadows are the one thing that does not survive the platform split intact.
          Platform.select({ web: 'shadow-sm shadow-black/5' }),
          className,
        )}
        {...props}
      />
    </TextClassContext.Provider>
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<typeof View> & React.RefAttributes<View>) {
  return <View className={cn('border-border flex-row flex-wrap items-center justify-between gap-3 border-b p-4', className)} {...props} />;
}

function CardTitle({ className, ...props }: React.ComponentProps<typeof Text>) {
  return <Text variant="heading" className={className} {...props} />;
}

function CardDescription({ className, ...props }: React.ComponentProps<typeof Text>) {
  return <Text variant="small" className={cn('text-muted-foreground', className)} {...props} />;
}

function CardContent({ className, ...props }: React.ComponentProps<typeof View> & React.RefAttributes<View>) {
  return <View className={cn('gap-3 p-4', className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<typeof View> & React.RefAttributes<View>) {
  return <View className={cn('bg-background border-border flex-row items-center justify-end gap-2 border-t px-4 py-3', className)} {...props} />;
}

export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
